import axios from "axios";
import React, { useState, useMemo } from "react";
import { BiBuildingHouse } from "react-icons/bi";
import { FaLocationDot, FaUsersGear } from "react-icons/fa6";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { IoClose } from "react-icons/io5";
import { MdOutlineDashboardCustomize, MdOutlineSensors, MdOutlineSupportAgent } from "react-icons/md";
import { TbReportMoney } from "react-icons/tb";
import { TiBell, TiChartLine, TiCogOutline, TiHomeOutline, TiPower, TiUser } from "react-icons/ti";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { Avatar, ConfigProvider, Menu } from "antd";
import { userRoleFormate } from "@/constant/userRole";
import { changeNavOpen, changeTheme } from "@/redux/slices/theme.slice";
import { setActiveDashboard, setIsLogged, setUserDetails } from "@/redux/slices/user.slice";
import { flare } from "@flareapp/js";

const NavBar = React.memo(() => {
  // hooks
  const { isNavOpen } = useSelector((state) => state.theme);
  const { userDetails } = useSelector((state) => state.user || {});
  const business = userDetails?.business?.[0] || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // states
  const [stateOpenKeys, setStateOpenKeys] = useState([]);

  const logOut = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout.",
      dangerMode: true,
      showCancelButton: true,
      confirmButtonText: "Confirm",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.get(`/user/logout`, {
            withCredentials: true,
          });
          localStorage.setItem("isLoggedIn", "no");
          dispatch(setActiveDashboard(""));
          dispatch(setIsLogged(false));
          dispatch(changeTheme(false));
          dispatch(setUserDetails({}));
          if (business?.businessUrl) {
            navigate("/" + business?.businessUrl);
          } else {
            navigate("/");
          }
        } catch (error) {
          flare.report(error);
          // eslint-disable-next-line no-console
          console.log(error.message);
        }
      }
    });
  };

  const generateMenuItems = (config, role, pathname, isCollapsed = false) => {
    return config
      .filter((item) => !item.authorizedRoles || item.authorizedRoles.includes(role))
      .map((item) => {
        let name = item.name;
        if (role !== "superAdmin" && name === "Business") name = "My Business";

        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const authorizedChildren = hasChildren ? item.children.filter((c) => c.authorizedRoles.includes(role)) : [];

        let children;
        if (authorizedChildren.length > 0) {
          if (isCollapsed) {
            children = [
              {
                key: `${item.name}-group`,
                label: (
                  <div>
                    <div className="pb-2 fw-bold">{name}</div>
                    <hr className="my-1 border-secondary opacity-25" />
                  </div>
                ),
                type: "group",
                children: generateMenuItems(authorizedChildren, role, pathname, false),
              },
            ];
          } else {
            children = generateMenuItems(authorizedChildren, role, pathname, false);
          }
        }

        const isActive = item.path && pathname === item.path;
        const isSubMenu = hasChildren && authorizedChildren.length > 0;

        return {
          key: isSubMenu ? `${item.path || item.name}-submenu` : (item.path || item.name),
          icon: item.icon,
          label: item.path ? (
            <Link to={item.path} style={{ textDecoration: "none", color: "inherit" }} className={isActive ? "text-primary fw-medium" : ""}>
              {name}
            </Link>
          ) : (
            <span className={isActive ? "text-primary fw-medium" : ""}>{name}</span>
          ),
          onClick: item.onClick ? () => item.onClick() : undefined,
          children: children,
          title: name,
        };
      });
  };

  const menuItems = useMemo(
    () => generateMenuItems(navigationConfig(logOut), userDetails?.role, location.pathname, isNavOpen),
    [userDetails?.role, location.pathname, isNavOpen],
  );

  const getLevelKeys = (items) => {
    const keys = {};
    const func = (itemsList, level = 1) => {
      itemsList.forEach((item) => {
        if (item.key) keys[item.key] = level;
        if (item.children) func(item.children, level + 1);
      });
    };
    func(items);
    return keys;
  };

  const levelKeys = useMemo(() => getLevelKeys(menuItems), [menuItems]);

  const onOpenChange = (openKeys) => {
    const currentOpenKey = openKeys.find((key) => stateOpenKeys.indexOf(key) === -1);
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys.filter((key) => key !== currentOpenKey).findIndex((key) => levelKeys[key] === levelKeys[currentOpenKey]);

      setStateOpenKeys(openKeys.filter((_, index) => index !== repeatIndex).filter((key) => levelKeys[key] <= levelKeys[currentOpenKey]));
    } else {
      setStateOpenKeys(openKeys);
    }
  };

  const logoOpen = business?.logo || "/img/logo.png";
  const logoClose = business?.logoShort || business?.logo || "/img/favicon.png";

  return (
    <>
      <div className={`navbar-overlay ${isNavOpen ? "" : "active"}`} onClick={() => dispatch(changeNavOpen(!isNavOpen))} />
      <div className={`az-sidebar ${isNavOpen ? "" : "active"}`}>
        <div className="close-container">
          <div className="close" onClick={() => dispatch(changeNavOpen(!isNavOpen))}>
            <IoClose />
          </div>
        </div>
        <div className="az-sidebar-header my-3 mx-3 text-center">
          <Link to="/" className="az-logo" onClick={() => dispatch(setActiveDashboard(""))}>
            {isNavOpen ? <img src={logoClose} alt="" style={{ height: "45px" }} /> : <img src={logoOpen} alt="" style={{ height: "60px" }} />}
          </Link>
        </div>

        {/* Profile section */}
        <div className={`az-sidebar-loggedin transition-all duration-300 ${isNavOpen ? "opacity-0 h-0 overflow-hidden m-0 p-0" : "mb-3"}`}>
          <div className="az-img-user online" style={{ minWidth: 36 }}>
            <Avatar src={userDetails?.avatar} size={40} />
          </div>
          <div className="media-body">
            <h6 title={userDetails?.name} className="mb-0">
              {(userDetails?.name?.length ?? 0) > 23 ? `${userDetails.name.slice(0, 21)}...` : userDetails?.name || "John Doe"}
            </h6>
            <span className="text-capitalize small text-muted">{userRoleFormate[userDetails?.role]}</span>
          </div>
        </div>

        <div className="az-sidebar-body ps-0 overflow-y-auto">
          <ConfigProvider
            theme={{
              components: {
                Menu: {
                  collapsedIconSize: 22,
                  iconSize: 18,
                  itemSelectedBg: "rgba(13, 110, 253, 0.1)",
                  itemBg: "transparent",
                  itemColor: "var(--color-black)",
                  itemSelectedColor: "#0d6efd",
                  itemHoverColor: "#0d6efd",
                  itemHoverBg: "rgba(13, 110, 253, 0.05)",
                  popupBg: "var(--bg-color)",
                },
              },
            }}
          >
            <Menu
              selectedKeys={[location.pathname]}
              mode="inline"
              className="border-0 bg-transparent"
              inlineCollapsed={isNavOpen}
              items={menuItems}
              openKeys={stateOpenKeys}
              onOpenChange={onOpenChange}
              onClick={(item) => {
                if (item.keyPath?.length <= 1) setStateOpenKeys([]);
              }}
            />
          </ConfigProvider>
        </div>
      </div>
    </>
  );
});

export default NavBar;

NavBar.displayName = "Navbar";
const navigationConfig = (logOut) => [
  {
    name: "Home",
    path: "/",
    icon: <TiHomeOutline />,
    authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
  },
  {
    name: "Business",
    path: "/business",
    icon: <BiBuildingHouse />,
    authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
    children: [
      {
        name: "All Businesses",
        path: "/business",
        authorizedRoles: ["superAdmin"],
      },
    ],
  },
  {
    name: "Manage Users",
    path: "/users",
    icon: <FaUsersGear />,
    authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
    children: [
      {
        name: "All Users",
        path: "/users",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
      },
      {
        name: "Manage Business Admins",
        path: "/users?role=businessAdmin",
        authorizedRoles: ["superAdmin"],
      },
      {
        name: "Manage Installers",
        path: "/users?role=installer",
        authorizedRoles: ["superAdmin", "businessAdmin"],
      },
      {
        name: "Manage Site Admins",
        path: "/users?role=admin",
        authorizedRoles: ["superAdmin", "businessAdmin"],
      },
      {
        name: "Manage Site Users",
        path: "/users?role=user",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
      },
      {
        name: "Manage Public Users",
        path: "/users?role=public",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
      },
    ],
  },
  {
    name: "Site Locations",
    path: "/site-locations",
    icon: <FaLocationDot />,
    authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
    children: [
      {
        name: "All Site Locations",
        path: "/site-locations",
        authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
      },
    ],
  },
  {
    name: "Devices",
    path: "/devices",
    icon: <MdOutlineSensors />,
    authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
    children: [
      {
        name: "All Devices",
        path: "/devices",
        authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
      },
      {
        name: "Manage Device Types",
        path: "/device-types",
        authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user"],
      },
    ],
  },
  {
    name: "Analysis & Reporting",
    path: "/data-analysis",
    icon: <TiChartLine />,
    authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
    children: [
      {
        name: "Data Analysis",
        path: "/data-analysis",
        authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
      },
      {
        name: "Electricity Report",
        path: "/billing-report",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
      },
      {
        name: "Water Report",
        path: "/water-report",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
      },
    ],
  },
  {
    name: "Notification",
    path: "/alarm-summary",
    icon: <TiBell />,
    authorizedRoles: ["superAdmin", "businessAdmin", "admin", "user", "public"],
    children: [
      {
        name: "System Alarm Summary",
        path: "/alarm-summary",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin", "user", "public"],
      },
      {
        name: "Notification Recipient List",
        path: "/recipient-list",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin", "user"],
      },
      {
        name: "Alarm Trigger History",
        path: "/alarm-history",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin", "user", "public"],
      },
    ],
  },
  {
    name: "Settings",
    path: "/settings",
    icon: <TiCogOutline />,
    authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
    children: [
      {
        name: "Change Password",
        path: "/settings",
        authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
      },
      {
        name: "Activity Log",
        path: "/activity-log",
        authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
      },
      {
        name: "Electricity Tariff",
        path: "/electricity-tariff",
        authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
      },
      {
        name: "Water Tariff",
        path: "/water-tariff",
        authorizedRoles: ["superAdmin"],
      },
      {
        name: "Manage System Parameters",
        path: "/parameters",
        authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin"],
      },
      {
        name: "Manage Formulas",
        path: "/formulas",
        authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin"],
      },
    ],
  },
  {
    name: "Dashboard",
    path: "/dashboard-list",
    icon: <MdOutlineDashboardCustomize />,
    authorizedRoles: ["superAdmin", "businessAdmin", "admin", "user", "public"],
    children: [
      {
        name: "Dashboard List",
        path: "/dashboard-list",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin", "user", "public"],
      },
      {
        name: "Assign Dashboard",
        path: "/assign-dashboard",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
      },
      {
        name: "Default Dashboard",
        path: "/default-dashboard-list",
        authorizedRoles: ["superAdmin"],
      },
    ],
  },
  {
    name: "Custom Reports",
    path: "/report-template-list",
    icon: <HiOutlineDocumentReport />,
    authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
    children: [
      {
        name: "Report Template List",
        path: "/report-template-list",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
      },
      {
        name: "Assign Report Template",
        path: "/assign-report-template",
        authorizedRoles: ["superAdmin", "businessAdmin"],
      },
      {
        name: "Generate Report",
        path: "/generate-report",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
      },
      {
        name: "Report List",
        path: "/report-list",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin"],
      },
    ],
  },
  {
    name: "Subscription",
    path: "/plan-list",
    icon: <TbReportMoney />,
    authorizedRoles: ["superAdmin"],
    children: [
      {
        name: "Plan List",
        path: "/plan-list",
        authorizedRoles: ["superAdmin"],
      },
      {
        name: "Assign Plan to Business",
        path: "/assign-plan",
        authorizedRoles: ["superAdmin"],
      },
      {
        name: "Subscription Summary",
        path: "/subscription-summary",
        authorizedRoles: ["superAdmin"],
      },
    ],
  },
  {
    name: "My Subscription",
    path: "/my-subscription",
    icon: <TbReportMoney />,
    authorizedRoles: ["businessAdmin"],
  },
  {
    name: "Support",
    path: "",
    icon: <MdOutlineSupportAgent />,
    authorizedRoles: ["superAdmin", "businessAdmin", "admin", "installer", "user", "public"],
    children: [
      {
        name: "Send Support Message",
        path: "/support-request",
        authorizedRoles: ["businessAdmin", "admin", "installer", "user", "public"],
      },
      {
        name: "Support Inbox",
        path: "/support-inbox",
        authorizedRoles: ["superAdmin", "businessAdmin", "admin", "installer", "user", "public"],
      },
    ],
  },

  {
    name: "Profile",
    path: "/profile",
    icon: <TiUser />,
    authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
  },
  {
    name: "Logout",
    path: null,
    icon: <TiPower />,
    authorizedRoles: ["superAdmin", "businessAdmin", "installer", "admin", "user", "public"],
    onClick: () => logOut(),
  },
];
